import React, { useState, useEffect, useMemo, useContext } from 'react';
import { HiOutlineBuildingStorefront } from 'react-icons/hi2';

import Layout from '../../components/Layout';
import useApi from '../../hooks/useApi';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const StockBadge = ({ stock }) => {
  const stockLevel = stock || 0;
  const styles = useMemo(() => {
    if (stockLevel > 20) return 'bg-green-100 text-green-800';
    if (stockLevel > 0) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  }, [stockLevel]);

  return (
    <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${styles}`}>
      {stockLevel} in stock
    </span>
  );
};

const StoreInventoryCard = ({ storeData, currencySymbol }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
    <div className="p-4 bg-gray-50 border-b border-gray-200">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{storeData?.store?.name}</h3>
          <p className="text-sm text-gray-500">{storeData?.store?.location}</p>
        </div>
        {storeData?.store?.code && (
          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-mono font-bold rounded">
            {storeData.store.code}
          </span>
        )}
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {storeData.inventory.map(({ product, stock }) => (
            <tr key={product._id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{product.sku}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 text-right">{currencySymbol}{Number(product.price).toFixed(2)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-center"><StockBadge stock={stock} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const StoreInventoryReportPage = () => {
  const api = useApi();
  const { settings } = useContext(AuthContext);
  const [reportData, setReportData] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currencySymbol = settings?.currencySymbol || '$';

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await api.get('stores');
        setStores(response.data || []);
      } catch (err) {
        console.error("Failed to fetch stores for filter", err);
      }
    };
    fetchStores();
  }, [api]);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const queryParams = selectedStore ? { storeId: selectedStore } : {};
        const response = await api.get('inventory/by-store', { queryParams });
        setReportData(response.data || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch inventory report.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [api, selectedStore]);

  return (
    <Layout title="Store Inventory Report">
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label htmlFor="store-filter" className="block text-sm font-medium text-gray-700">Filter by Store</label>
            <select
              id="store-filter"
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="">All Stores</option>
              {stores.map(store => (
                <option key={store._id} value={store._id}>{store.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}
      {!loading && !error && (
        reportData.length > 0 ? (
          reportData.map(storeData => (
            <StoreInventoryCard key={storeData?.store?._id} storeData={storeData} currencySymbol={currencySymbol} />
          ))
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <HiOutlineBuildingStorefront className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No inventory data found</h3>
            <p className="mt-1 text-sm text-gray-500">There is no inventory to display for the selected store(s).</p>
          </div>
        )
      )}
    </Layout>
  );
};

export default StoreInventoryReportPage;